import { defineStore } from 'pinia';
import { apiCall } from '@/utils/globalFunctions';
import store from '@/store/store';
import { debounce } from 'lodash';

export const usePaymentStore = defineStore('payments', {
  state: () => {
    const userId = store.state.user?.user_id || null;
    const storageKey = userId ? `paymentStore_${userId}` : 'paymentStore';
    const persistedState = getPersistedState(storageKey);
    return {
      userId,
      payments: persistedState.payments || [],
      currentPage: persistedState.currentPage || 1,
      itemsPerPage: persistedState.itemsPerPage || 10,
      totalPages: persistedState.totalPages || 1,
      totalRows: persistedState.totalRows || 0,
      startDate: persistedState.startDate || null,
      endDate: persistedState.endDate || null,
      totalPayment: persistedState.totalPayment || 0,
      currencyTotals: persistedState.currencyTotals || 0,
      lastFivePayments: [],
      loading: false,
      error: null,
    };
  },

  getters: {
    filteredPayments: (state) => state.payments,
    filteredTotalPayment: (state) => state.totalPayment,
    filteredCurrencyTotals: (state) => state.currencyTotals,
  },

  actions: {
    persistState() {
      setPersistedState(`paymentStore_${this.userId}`,{
        payments: this.payments,
        currentPage: this.currentPage,
        itemsPerPage: this.itemsPerPage,
        totalPages: this.totalPages,
        totalRows: this.totalRows,
        startDate: this.startDate,
        endDate: this.endDate,
        totalPayment: this.totalPayment,
        currencyTotals: this.currencyTotals
      });
    },

    setUserId(userId) {
      this.userId = userId;
      this.reset();
      if (userId) {
        this.fetchPayments();
      }
    },
    resetFilters() {
      this.startDate = null;
      this.endDate = null;
      this.currentPage = 1; // Reset to first page
    },

    reset() {
      this.$reset();
      if (this.userId) {
        localStorage.removeItem(`paymentStore_${this.userId}`);
      }
    },
    // Debounced fetch function (unchanged)
    _debouncedFetch: debounce(function (params, options) {
      return (async () => {
        const response = await apiCall(
          {
            url: '/Payment/getPayments',
            method: options.method,
            dataType: 'json',
            data: params,
          },
          store
        );

        if (Array.isArray(response.data)) {
          if (options.persist) {
            this.payments = response.data;
            this.totalPages = response.totalPages || 1;
            this.totalRows = response.totalRows || 0;
            this.persistState();
          }
          return response.data;
        }
        throw new Error(response.message || 'Invalid response format');
      })();
    }, 300),

    // Main private fetch function (unchanged)
    async _fetchData(params = {}, options = {}) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
        return [];
      }

      const defaults = {
        resetFilters: false,
        clearCache: false,
        persist: true,
        debounceTime: 0,
        method: 'GET',
      };

      const finalOptions = { ...defaults, ...options };

      if (finalOptions.clearCache) {
        this.payments = [];
        this.totalRows = 0;
      }

      if (finalOptions.resetFilters) {
        this.resetFilters();
      }

      this.loading = true;
      this.resetError();

      try {
        if (finalOptions.debounceTime > 0) {
          return await this._debouncedFetch(params, finalOptions);
        } else {
          const response = await apiCall(
            {
              url: '/Payment/getPayments',
              method: finalOptions.method,
              dataType: 'json',
              data: params,
            },
            store
          );

          if (Array.isArray(response.data)) {
            if (finalOptions.persist) {
              this.payments = response.data;
              this.totalPages = response.totalPages || 1;
              this.totalRows = response.totalRows || 0;
              this.persistState();
            }
            this.totalPayment = response.totalPayment;
            this.currencyTotals = response.currencyTotals;
            return response.data;
          }
          throw new Error(response.message || 'Invalid response format');
        }
      } catch (err) {
        this.setError(err, 'Failed to fetch payments', 'FETCH_PAYMENTS_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },

    // Public API functions
    async fetchPayments(params = {}, options = {}) {
      const {
        page = this.currentPage,
        pageSize = this.itemsPerPage,
        startDate = this.startDate || null,
        endDate = this.endDate || null,
        invoiceId = null,
      } = params;

      const data = await this._fetchData(
        { startDate, endDate, page, pageSize, invoiceId },
        {
          ...options,
          debounceTime: 300, // Default debounce for regular fetches
        }
      );


      this.currentPage = page;
      this.itemsPerPage = pageSize;
      this.startDate = startDate;
      this.endDate = endDate;

      return data;
    },

    async fetchLastFivePayments() {
      try {
        const today = getDefaultEndDate();
        const thirtyDaysAgo = getDefaultStartDate(30);

        const response = await this._fetchData(
          {
            page: 1,
            pageSize: 5,
            startDate: thirtyDaysAgo,
            endDate: today,
          },
          {
            persist: true,
            debounceTime: 0
          }
        );

        if (response) {
          this.lastFivePayments = response;
          return response;
        }

      } catch (err) {
        this.setError(err, 'Failed to fetch last five payments', 'LAST_FIVE_FETCH_FAILED');
        throw err;
      }
    },

    async addPayment(paymentData) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      try {
        const response = await apiCall({
          method: 'POST',
          url: '/Payment/addPayment',
          data: paymentData,
          dataType: 'json',
        }, store);

        if (response.status === 'success') {
          await this.fetchPayments({ page: 1 }); // fresh fetch
          return response;
        }
        throw new Error(response.message);
      } catch (err) {
        this.setError(err, 'Failed to add payment', 'ADD_PAYMENT_FAILED');
        throw err;
      }
    },

    async updatePayment(paymentData) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      try {
        const response = await apiCall({
          method: 'POST',
          url: '/Payment/updatePayment',
          data: paymentData,
          dataType: 'json',
        }, store);

        if (response.status === 'success') {
          await this.fetchPayments(); // Refresh full list
          return response;
        }
        throw new Error(response.message);
      } catch (err) {
        this.setError(err, 'Failed to update payment', 'UPDATE_PAYMENT_FAILED');
        throw err;
      }
    },

    async deletePayment(paymentId) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      try {
        const response = await apiCall({
          method: 'POST',
          url: '/Payment/deletePayment',
          data: { paymentId },
          dataType: 'json',
        }, store);

        if (response.status === 'success') {
          await this.fetchPayments(); // Refresh full list
          return response;
        }
        throw new Error(response.message);
      } catch (err) {
        this.setError(err, 'Failed to delete payment', 'DELETE_PAYMENT_FAILED');
        throw err;
      }
    },

    async refreshPayments() {
      return this._fetchData(
        {},
        {
          clearCache: true,
          resetFilters: false,
          debounceTime: 0 // No debounce for refresh
        }
      );
    },

    setStartDate(startDate) {
      this.startDate = startDate;
    },

    setEndDate(endDate) {
      this.endDate = endDate;
    },
    setError(err, fallbackMessage, fallbackCode) {
      this.error = {
        message: err?.message || fallbackMessage,
        code: err?.code || fallbackCode,
        timestamp: new Date().toISOString(),
      };
    },
    resetError() {
      this.error = null;
    },
  },
});

// Helper functions
function getPersistedState(storageKey) {
  try {
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Failed to parse stored payments state', error);
    return {};
  }
}

function setPersistedState(storageKey, state) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to persist payments state', error);
  }
}

function getDefaultStartDate(daysAgo = 30) {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toISOString().split('T')[0];
}

function getDefaultEndDate() {
  return new Date().toISOString().split('T')[0];
}
